import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Timestamp} from '@wandb/weave/components/Timestamp';
import React, {useMemo} from 'react';
import {QueryResult} from 'react-apollo';
import {useParams} from 'react-router-dom';

import {
  Exact,
  FetchArtifactCollectionTriggersQuery,
  FetchProjectTriggersQuery,
} from '../../../../generated/graphql';
// @todo: Migrate to 'BasicTable' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Table} from '../../Table';
import {BaseTriggerDrawerInit} from '../CreateTriggerDrawer/EventStore';
import {EventTypeChip} from '../EventTypeChip';
import {TriggerActions} from './TriggerActions';
import {TriggerName} from './TriggerName';
import {getActionInfo, getTriggersDetails} from './utilData';

export type TriggersTableProps = {
  projectTriggersQuery: QueryResult<
    FetchProjectTriggersQuery,
    Exact<{
      entityName: string;
      projectName: string;
    }>
  >;
  artifactCollectionTriggersQuery?: QueryResult<FetchArtifactCollectionTriggersQuery>;
  baseTriggerDrawerInit: BaseTriggerDrawerInit;
  readOnly: boolean;
};

export const TriggersTable = ({
  projectTriggersQuery,
  artifactCollectionTriggersQuery,
  baseTriggerDrawerInit,
  readOnly,
}: TriggersTableProps) => {
  const {entityName} = useParams<{entityName: string}>();
  const rows = useMemo(() => {
    const triggersData = getTriggersDetails(
      projectTriggersQuery,
      artifactCollectionTriggersQuery
    );
    return (
      triggersData
        ?.map(trigger => {
          const {actionTypeName} = getActionInfo(trigger);
          const date = new Date(trigger.createdAt);
          const createdDate = `${date.toDateString()}, at ${date.toLocaleTimeString(
            [],
            {hour: '2-digit', minute: '2-digit'}
          )}`;

          return {
            row: {
              id: trigger.id,
              name: <TriggerName trigger={trigger} />,
              triggerEventType: (
                <Tailwind style={{height: '100%', width: '100%'}}>
                  <div className="flex h-full flex-col items-start justify-center">
                    <EventTypeChip
                      eventType={trigger.triggeringCondition?.eventType}
                    />
                  </div>
                </Tailwind>
              ),
              triggerActionType: (
                <Tailwind style={{height: '100%', width: '100%'}}>
                  <div className="flex h-full flex-col items-start justify-center">
                    {actionTypeName}
                  </div>
                </Tailwind>
              ),
              dateCreated: (
                <Tailwind style={{height: '100%', width: '100%'}}>
                  <div className="flex h-full flex-col items-start justify-center">
                    {createdDate}
                  </div>
                </Tailwind>
              ),
              lastExecution: (
                <Tailwind style={{height: '100%', width: '100%'}}>
                  <div className="flex h-full flex-col items-start justify-center">
                    {trigger.lastExecutedAt && (
                      <Timestamp
                        value={trigger.lastExecutedAt}
                        format="relative"
                      />
                    )}
                  </div>
                </Tailwind>
              ),
              deleteTrigger: (
                //  Override general cell alignment properties for actions' overflow menu
                <TriggerActions
                  trigger={trigger}
                  refetchProjectTriggers={projectTriggersQuery?.refetch}
                  refetchCollectionTriggers={
                    artifactCollectionTriggersQuery?.refetch
                  }
                  baseTriggerDrawerInit={baseTriggerDrawerInit}
                  readOnly={readOnly}
                />
              ),
            },
            searchString: trigger.name,
          };
        })
        .filter(r => r.row.id != null) || []
    );
  }, [
    projectTriggersQuery,
    artifactCollectionTriggersQuery,
    baseTriggerDrawerInit,
    readOnly,
  ]);
  const columns = [
    {
      Header: 'AUTOMATION',
      accessor: 'name',
      resizable: true,
    },
    {
      Header: 'EVENT TYPE',
      accessor: 'triggerEventType',
    },
    {
      Header: 'ACTION TYPE',
      accessor: 'triggerActionType',
    },
    {
      Header: 'DATE CREATED',
      accessor: 'dateCreated',
    },
    {
      Header: 'LAST EXECUTION',
      accessor: 'lastExecution',
    },
    {
      Header: '',
      accessor: 'deleteTrigger',
      width: 200,
    },
  ];
  const acTriggersLoading = artifactCollectionTriggersQuery
    ? artifactCollectionTriggersQuery.data == null
    : false;

  const noDataComponent = () => (
    <Tailwind style={{height: '100%', width: '100%'}}>
      <div className="flex h-full items-start justify-center pt-16 pb-16">
        No rows found
      </div>
    </Tailwind>
  );

  return (
    <Table
      data={rows}
      noDataComponent={noDataComponent}
      loading={projectTriggersQuery?.data == null || acTriggersLoading}
      columns={columns}
      pageSize={20}
      defaultSorted={[{id: 'name', desc: true}]}
    />
  );
};
